import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import DialogProvider from "@components/dialog_provider"
import { useCustomerPageContext } from "@contexts/customer"
import { customersVerifyUser, VerifyUserEmail } from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import { CustomerPerson } from "__generated__/graphql"

export default function CustomerVerifyEmail({
  isOpen,
  setClose,
  selectedRow
}: {
  isOpen: boolean
  setClose: () => void
  selectedRow?: CustomerPerson
}) {
  const { translate, setUsers, users } = useCustomerPageContext()
  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()

  const {
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<VerifyUserEmail>({
    mode: "all"
  })
  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }
  useEffect(() => {
    if (selectedRow) {
      reset({
        isVerified: true
      })
    }
  }, [selectedRow])

  async function onSubmit() {
    if (!selectedRow?._id) {
      return
    }
    setLoading(true)
    const { isSuccess, errorMessage } = await customersVerifyUser({
      _id: selectedRow._id,
      customerId: selectedRow.customer,
      isVerified: true
    })
    setLoading(false)
    if (!isSuccess && !!errorMessage) {
      showErrorToast(errorMessage)
      return
    }
    setUsers?.(
      users.map((user) => {
        if (user._id === selectedRow?._id) {
          return {
            ...user,
            isEmailVerified: true
          }
        }
        return user
      })
    )
    queryClient.invalidateQueries({ queryKey: ["customer-data-table"] })
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("verify_email")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("verify"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack justifyContent={"center"} alignItems={"center"} flex={1}>
        <Typography align="center">{translate("verify_email_text")}</Typography>
      </Stack>
    </DialogProvider>
  )
}
